/**
 * Gallery Session Tracker
 * Tracks time spent on the gallery page for VIP progress metrics.
 * A session counts as 1 gallery visit if the user stays 3+ minutes.
 *
 * - On mount: starts a session
 * - Every 60s: sends heartbeat to keep session alive
 * - On unmount/navigation: ends session
 * - On tab close: uses keepalive fetch to end session
 * - On visibility hidden 5+ min: ends session; on restore: starts new
 */

import { useEffect, useRef } from 'react'
import {
  startGallerySession,
  endGallerySession,
  heartbeatGallerySession,
  endGallerySessionKeepalive
} from '@/services/vipService'

const HEARTBEAT_INTERVAL_MS = 60_000
const HIDDEN_TIMEOUT_MS = 5 * 60_000

export function useGallerySessionTracker() {
  const sessionIdRef = useRef<string | null>(null)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hiddenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const startSession = async () => {
    // Don't start if we already have an active session
    if (sessionIdRef.current) return

    try {
      const { session_id } = await startGallerySession()
      sessionIdRef.current = session_id

      // Start heartbeat
      heartbeatRef.current = setInterval(() => {
        if (sessionIdRef.current) {
          heartbeatGallerySession(sessionIdRef.current).catch(() => {})
        }
      }, HEARTBEAT_INTERVAL_MS)
    } catch {
      // Silently fail - gallery still works without tracking
    }
  }

  const stopSession = async () => {
    // Clear heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }

    if (sessionIdRef.current) {
      try {
        await endGallerySession(sessionIdRef.current)
      } catch {
        // Silently fail
      }
      sessionIdRef.current = null
    }
  }

  const stopSessionSync = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }

    if (sessionIdRef.current) {
      endGallerySessionKeepalive(sessionIdRef.current)
      sessionIdRef.current = null
    }
  }

  useEffect(() => {
    startSession()

    // Handle tab close
    const handleBeforeUnload = () => {
      stopSessionSync()
    }

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - start a timer. If hidden for 5+ min, end session.
        hiddenTimerRef.current = setTimeout(() => {
          stopSession()
        }, HIDDEN_TIMEOUT_MS)
      } else {
        // Tab visible again
        if (hiddenTimerRef.current) {
          clearTimeout(hiddenTimerRef.current)
          hiddenTimerRef.current = null
        }
        // If session was ended due to being hidden, start a new one
        if (!sessionIdRef.current) {
          startSession()
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      if (hiddenTimerRef.current) {
        clearTimeout(hiddenTimerRef.current)
      }

      // On unmount (navigation away), end the session synchronously
      stopSessionSync()
    }
  }, [])
}
